/**
 * Generated With: pnpm x packages/api/tools/generate-shopify-rest-model.ts https://shopify.dev/api/admin/rest/reference/inventory/inventorylevel
 * API Version: 2022-01
 */

import { UniqueValidationId } from "../../database/validations/keys";
import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyInventoryItemKey,
  ShopifyInventoryLevelKey,
  ShopifyInventoryLevelToInventoryItemKey,
  ShopifyInventoryLevelToLocationKey,
  ShopifyInventoryLevelToShopKey,
  ShopifyInventoryLevelUniquenessValidationKey,
  ShopifyInventoryQuantityKey,
  ShopifyInventoryQuantityToInventoryLevelKey,
  ShopifyLocationKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyInventoryLevelKey,
  defaultFilterableApiIdentifiers: ["adminGraphqlApiId", "available"],
  "2022-01": {
    name: "Shopify Inventory Level",
    fields: [
      {
        name: "Available",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "available" } },
      },
      {
        name: "Inventory Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryLevelToInventoryItemKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "inventory_item_id" } },
        configuration: {
          relatedModelKey: ShopifyInventoryItemKey,
        },
        validations: {
          [ShopifyInventoryLevelUniquenessValidationKey]: {
            key: ShopifyInventoryLevelUniquenessValidationKey,
            specID: UniqueValidationId,
            configuration: {
              values: {
                scopeByField: ShopifyInventoryLevelToLocationKey,
              },
            },
          },
        },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryLevelToLocationKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "location_id" } },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryLevelToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "custom", resource: "inventory" },
  },
  "2023-07": {
    fields: [
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "admin_graphql_api_id",
            graphQLFieldKey: "id",
          },
        },
      },
      {
        name: "Can Deactivate",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_level_canDeactivate",
            graphQLFieldKey: "canDeactivate",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Deactivation Alert",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_level_deactivationAlert",
            graphQLFieldKey: "deactivationAlert",
            graphQLOnlyField: true,
          },
        },
      },
    ],
  },
  "2025-04": {
    graphQLTypename: "InventoryLevel",
    fields: [
      {
        name: "Available",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "available",
            availableInWebhook: ["inventory_levels/connect", "inventory_levels/update"],
          },
        },
      },
      {
        name: "Inventory Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryLevelToInventoryItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "inventory_item_id",
            graphQLFieldKey: "item",
            graphQLSelection: "id",
            availableInWebhook: ["inventory_levels/connect", "inventory_levels/update", "inventory_levels/disconnect"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyInventoryItemKey,
        },
        validations: {
          [ShopifyInventoryLevelUniquenessValidationKey]: {
            key: ShopifyInventoryLevelUniquenessValidationKey,
            specID: UniqueValidationId,
            configuration: {
              values: {
                scopeByField: ShopifyInventoryLevelToLocationKey,
              },
            },
          },
        },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryLevelToLocationKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "location_id",
            graphQLFieldKey: "location",
            graphQLSelection: "id",
            availableInWebhook: ["inventory_levels/connect", "inventory_levels/update", "inventory_levels/disconnect"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "updated_at",
            graphQLFieldKey: "updatedAt",
            availableInWebhook: ["inventory_levels/connect", "inventory_levels/update"],
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "created_at", graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "admin_graphql_api_id",
            graphQLFieldKey: "id",
            availableInWebhook: ["inventory_levels/connect", "inventory_levels/update"],
          },
        },
      },
      {
        name: "Can Deactivate",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_level_canDeactivate",
            graphQLFieldKey: "canDeactivate",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Deactivation Alert",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_level_deactivationAlert",
            graphQLFieldKey: "deactivationAlert",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Quantities",
        fieldType: FieldType.HasMany,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "quantities",
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyInventoryQuantityKey,
          inverseFieldKey: ShopifyInventoryQuantityToInventoryLevelKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "inventoryItems",
      resource: "inventoryItem.inventoryLevels",
    },
  },
};

export const ShopifyInventoryLevel = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
  withDefaultFieldsOnly,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
  withDefaultFieldsOnly?: boolean;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel, withDefaultFieldsOnly);
